import { css } from "@emotion/react"
import { Heading } from "@planningcenter/doxy-web"
import cx from "classnames"
import { useEffect } from "react"
import { CustomSelect } from "source/giving/components/CustomSelect"
import { Field } from "source/giving/components/Field"
import { Icon } from "source/shared/components/Icon"
import { BLANK_FUND_ID } from "../constants"
import { useDonationFormContext } from "../hooks/useDonationFormContext"
import { extractMessagesFrom } from "../stores/validations"
import { Designation, Fund, ValidationErrors } from "../types"
import { DesignationRow } from "./DesignationRow"

export function getFieldId(type: "amount" | "fund", index: number) {
  return `designation-${index}-${type}`
}

type FundSelectionProps = {
  designations: Designation[]
  errors: ValidationErrors
  focusedDesignationIndex: number
}

export function FundSelection({
  designations,
  errors,
  focusedDesignationIndex,
}: FundSelectionProps) {
  const { config, dispatch } = useDonationFormContext()

  const { funds } = config
  const canSplitDonation = designations.length < funds.length
  const isSplitDonation = designations.length > 1
  const hasBlankDesignation = designations.some(
    (d) => d.fundId === BLANK_FUND_ID,
  )
  const hasOneAvailableFund = funds.length === 1
  const firstFund = funds[0]

  const isFundAvailable = ({ id }: Fund) =>
    !designations.some((d) => d.fundId === id)

  const addDesignation = () => {
    const availableFundIds = funds.filter(isFundAvailable).map((f) => f.id)

    dispatch({
      type: "ADD_DESIGNATION",
      availableFundIds,
    })
  }

  const removeDesignation = (fundId: string) => {
    dispatch({ type: "REMOVE_DESIGNATION", fundId })
  }

  const handleFundChange = (fundId: string, newFundId: string) => {
    dispatch({ type: "UPDATE_DESIGNATION_FUND", fundId, newFundId, config })
  }

  const handleAmountChange = (fundId: string, amount: string) => {
    dispatch({ type: "UPDATE_DESIGNATION_AMOUNT", fundId, amount, config })
  }

  const handleBlur = () => dispatch({ type: "INPUT_BLURRED", config })

  // handle focus after add/remove designation
  useEffect(() => {
    if (focusedDesignationIndex >= 0) {
      const fundElement = document.getElementById(
        getFieldId("fund", focusedDesignationIndex),
      )
      fundElement?.focus()
    }
  }, [focusedDesignationIndex, isSplitDonation])

  return (
    <div>
      <Heading level={3} text="Fund" />
      <div className="mt-2 d-f fd-c g-2">
        {!isSplitDonation &&
          (hasOneAvailableFund ? (
            <div className="br-8p gc-tint9 p-2">
              <span className="fw-600">{firstFund.name}</span>
              {!!firstFund.description?.trim() && (
                <div className="mt-4p fs-4">{firstFund.description}</div>
              )}
            </div>
          ) : (
            <FundSelector
              fundId={designations[0].fundId}
              funds={funds}
              onChange={handleFundChange}
              onBlur={handleBlur}
              errors={errors}
            />
          ))}
        {isSplitDonation && (
          <div
            className="d-f fd-c"
            css={css`
              gap: 0.75rem;
            `}
          >
            {designations.map((d, index) => {
              const availableFunds = funds.filter(
                (fund) => fund.id === d.fundId || isFundAvailable(fund),
              )

              return (
                <DesignationRow
                  key={d.fundId}
                  index={index}
                  fundId={d.fundId}
                  amount={d.amount}
                  funds={availableFunds}
                  onFundChange={(newFundId) =>
                    handleFundChange(d.fundId, newFundId)
                  }
                  onAmountChange={(amount) =>
                    handleAmountChange(d.fundId, amount)
                  }
                  onRemove={() => removeDesignation(d.fundId)}
                  onAmountBlur={handleBlur}
                  errors={errors}
                />
              )
            })}
          </div>
        )}
        {canSplitDonation && (
          <AddDesignationButton
            onClick={addDesignation}
            disabled={hasBlankDesignation}
          />
        )}
      </div>
    </div>
  )
}

interface FundSelectorProps {
  errors: ValidationErrors
  fundId: string
  funds: Fund[]
  onChange: (fundId: string, newFundId: string) => void
  onBlur: () => void
}

function FundSelector({
  errors,
  fundId,
  funds,
  onChange,
  onBlur,
}: FundSelectorProps) {
  const FIELD_ID = getFieldId("fund", 0)

  const fundErrors = extractMessagesFrom(errors, `designation_${fundId}_fund`)

  return (
    <Field errors={fundErrors}>
      {({ isValid, errorId }) => (
        <>
          <label className="visually-hidden" htmlFor={FIELD_ID}>
            Fund
          </label>
          <CustomSelect
            id={FIELD_ID}
            value={fundId}
            onChange={(newFundId) => onChange(fundId, newFundId)}
            onBlur={onBlur}
            aria-invalid={!isValid}
            aria-describedby={errorId}
            placeholder="Choose a fund"
          >
            {fundId === BLANK_FUND_ID && (
              <CustomSelect.Option value={BLANK_FUND_ID}>
                Choose a fund
              </CustomSelect.Option>
            )}
            {funds.map((fund) => (
              <CustomSelect.Option
                key={fund.id}
                value={fund.id}
                description={fund.description}
              >
                {fund.name}
              </CustomSelect.Option>
            ))}
          </CustomSelect>
        </>
      )}
    </Field>
  )
}

function AddDesignationButton({
  disabled,
  onClick,
}: {
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className={cx(
        "btn text-btn compact-btn d-f ai-c mr-a c-tint1 fw-500 bw-0 pr-1",
        disabled && "outline-btn disabled-btn",
      )}
      css={css`
        border-radius: 1.5rem;
      `}
      onClick={onClick}
      disabled={disabled}
    >
      <span
        className={cx(
          "btn minor-btn badge-btn p-0 mr-1 c-tint1",
          disabled && "disabled-btn pe-n",
        )}
        css={css`
          aspect-ratio: 1;
        `}
      >
        <Icon symbol="general#plus" aria-hidden />
      </span>{" "}
      Give to another fund
    </button>
  )
}
